import { memo } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import type { DocumentReference } from "fhir/r5";
import type { DocumentCategory } from "../../types";
import type { CategoryStatus } from "../../helpers/documentsChecklistHelpers";
import { CategoryCard } from "./CategoryCard";
import type { RejectTarget } from "./DocumentTile";

export interface DocumentsChecklistRow {
  category: DocumentCategory;
  docs: DocumentReference[];
  status: CategoryStatus;
}

interface DocumentsChecklistSectionProps {
  rows: DocumentsChecklistRow[];
  pendingCount: number;
  canVerifyActions: boolean;
  organizationId?: string;
  busyDocId: string | null;
  approvingAll: boolean;
  inheritedDocIds?: string[];
  onView: (doc: DocumentReference, title: string) => void;
  onApprove: (doc: DocumentReference, title: string) => void;
  onReject: (target: RejectTarget) => void;
  onApproveAll: () => void;
  onRevokeApproval?: (doc: DocumentReference, title: string) => void;
  onRevokeRejection?: (doc: DocumentReference, title: string) => void;
}

export const DocumentsChecklistSection = memo(
  function DocumentsChecklistSection({
    rows,
    pendingCount,
    canVerifyActions,
    organizationId,
    busyDocId,
    approvingAll,
    inheritedDocIds,
    onView,
    onApprove,
    onReject,
    onApproveAll,
    onRevokeApproval,
    onRevokeRejection,
  }: DocumentsChecklistSectionProps) {
    return (
      <Stack
        spacing={1.25}
        sx={{ height: "100%", minHeight: 0, overflow: "hidden" }}
      >
        {canVerifyActions && pendingCount > 0 && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{
              flexShrink: 0,
              px: 1.25,
              py: 1,
              borderRadius: 1.5,
              border: 1,
              borderColor: "grey.200",
              bgcolor: "grey.50",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {pendingCount} pendente
              {pendingCount === 1 ? "" : "s"}
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={
                approvingAll ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <VerifiedOutlinedIcon fontSize="small" />
                )
              }
              disabled={approvingAll || Boolean(busyDocId)}
              onClick={onApproveAll}
              sx={{ textTransform: "none" }}
            >
              {approvingAll
                ? "Aprovando…"
                : `Aprovar todos (${pendingCount})`}
            </Button>
          </Stack>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gridTemplateRows: { xs: "auto", sm: "1fr 1fr" },
            gap: 1.25,
            overflow: { xs: "auto", sm: "hidden" },
            WebkitOverflowScrolling: "touch",
          }}
        >
          {rows.map(({ category, docs, status }) => (
            <Box
              key={category.code}
              sx={{ minHeight: { xs: 220, sm: 0 }, minWidth: 0 }}
            >
              <CategoryCard
                category={category}
                docs={docs}
                status={status}
                organizationId={organizationId}
                canVerify={canVerifyActions}
                busyDocId={busyDocId}
                approvingAll={approvingAll}
                inheritedDocIds={inheritedDocIds}
                onView={onView}
                onApprove={onApprove}
                onReject={onReject}
                onRevokeApproval={onRevokeApproval}
                onRevokeRejection={onRevokeRejection}
              />
            </Box>
          ))}
        </Box>
      </Stack>
    );
  },
);
